import type { NavLeaf, NavSection } from '../config/nav';

/**
 * Which section of the Super Admin's phone drawer was left open (Warren, 21 October 2026, item M: "Sections collapse;
 * the open one is remembered"), and which groups of anyone's menu are open (Warren, 28 October 2026: "Groups open on
 * click, remember open or closed per user, and open automatically when you're on a page inside them. Same structure in
 * the phone drawer."). Both are kept per user in this browser's localStorage: the Api has no menu preference to hold
 * them across devices. Storage can be blocked (a private window, cleared site data), so every read and write is
 * guarded and the menu works without it: groups then open only on the page inside them or on a click this visit.
 */
const SECTION_KEY = 'evi.admin.menu.openSection';
const sectionKey = (userId?: string | null) => (userId ? `${SECTION_KEY}.${userId}` : SECTION_KEY);

export function readOpenSection(storage: Pick<Storage, 'getItem'> | null = safeStorage(), userId?: string | null): string | null {
  try { return storage?.getItem(sectionKey(userId)) ?? null; } catch { return null; }
}

export function writeOpenSection(id: string | null, storage: Pick<Storage, 'setItem' | 'removeItem'> | null = safeStorage(), userId?: string | null): void {
  try {
    if (id) storage?.setItem(sectionKey(userId), id);
    else storage?.removeItem(sectionKey(userId));
  } catch { /* not remembered this time */ }
}

/** The remembered section if it is still in this menu, else null. */
export function rememberedSection(sectionIds: readonly string[], storage?: Pick<Storage, 'getItem'> | null, userId?: string | null): string | null {
  const id = readOpenSection(storage === undefined ? safeStorage() : storage, userId);
  return id && sectionIds.includes(id) ? id : null;
}

/** Each group's open (true) or closed (false) state, by menuGroupKey. A group never touched has no entry. */
export type GroupMemory = Readonly<Record<string, boolean>>;
export const GROUP_MEMORY_PREFIX = 'evi.admin.menu.groups.v1.';

/** The storage key for this user's groups; null without a user, so nothing is stored for no one. */
export function groupMemoryKey(userId?: string | null): string | null {
  return userId ? `${GROUP_MEMORY_PREFIX}${userId}` : null;
}

/** A group's key: its section and its own id, so the same group id in two sections is two groups. */
export const menuGroupKey = (sectionId: string, groupId: string) => `${sectionId}/${groupId}`;

/** Only an object of true and false survives; anything else stored there reads as nothing remembered. */
export function parseGroupMemory(raw: string | null | undefined): GroupMemory {
  if (!raw) return {};
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).filter(([, v]) => typeof v === 'boolean')) as GroupMemory;
  } catch { return {}; }
}

export function readGroupMemory(userId?: string | null, storage: Pick<Storage, 'getItem'> | null = safeStorage()): GroupMemory {
  const key = groupMemoryKey(userId);
  if (!key) return {};
  try { return parseGroupMemory(storage?.getItem(key)); } catch { return {}; }
}

/**
 * This user's groups as storage holds them now, for a click to merge into: another tab may have changed them since
 * this one read them, and writing this tab's old copy back would erase that. This tab's copy when there is nothing to
 * read: no user, or storage blocked.
 */
export function latestGroupMemory(userId: string | null | undefined, cached: GroupMemory, storage: Pick<Storage, 'getItem'> | null = safeStorage()): GroupMemory {
  const key = groupMemoryKey(userId);
  if (!key || !storage) return cached;
  try { return parseGroupMemory(storage.getItem(key)); } catch { return cached; }
}

export function writeGroupMemory(userId: string | null | undefined, memory: GroupMemory, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  const key = groupMemoryKey(userId);
  if (!key) return;
  try { storage?.setItem(key, JSON.stringify(memory)); } catch { /* not remembered this time */ }
}

/**
 * Whether a group is open. On a page inside it, it is open, unless it was closed by a click while on this page (so the
 * click always does something). Anywhere else it is open only if it was left open. A group starts closed.
 */
export function isGroupOpen(o: { remembered: boolean | undefined; holdsCurrentPage: boolean; closedOnThisPage: boolean }): boolean {
  if (o.holdsCurrentPage) return !o.closedOnThisPage;
  return o.remembered === true;
}

/** The memory after a click on one group: that group flips, every other group stays as it was. */
export function toggleGroup(memory: GroupMemory, key: string, wasOpen: boolean): GroupMemory {
  return { ...memory, [key]: !wasOpen };
}

/**
 * Whether a menu address is the page shown. An address with a query (the EVI shortcuts' /chargers?scope=network) is
 * that page only with those parameters; any other address also holds the pages under it (/rfid holds /rfid/assignments).
 */
export function matchesMenuHref(currentPath: string, href: string, search = ''): boolean {
  const q = href.indexOf('?');
  if (q >= 0) {
    if (currentPath !== href.slice(0, q)) return false;
    const have = new URLSearchParams(search);
    for (const [k, v] of new URLSearchParams(href.slice(q + 1))) if (have.get(k) !== v) return false;
    return true;
  }
  if (currentPath === href) return true;
  if (currentPath.startsWith(href)) {
    const next = currentPath[href.length];
    return next === undefined || next === '/';
  }
  return false;
}

export function groupHoldsPage(children: ReadonlyArray<Pick<NavLeaf, 'href'>>, currentPath: string, search = ''): boolean {
  return children.some((c) => matchesMenuHref(currentPath, c.href, search));
}

/**
 * The section and group holding the page shown. The EVI shortcuts repeat leaves that also sit in the Super Admin's
 * own sections, so the page is looked for in those first: the highlight and the drawer go to Network, not to EVI.
 */
export function activeMenuLocation(
  sections: ReadonlyArray<Pick<NavSection, 'id' | 'items'>>, currentPath: string, search = '',
): { sectionId: NavSection['id']; parentId: string | null } | null {
  const ordered = [...sections.filter((s) => s.id !== 'evi'), ...sections.filter((s) => s.id === 'evi')];
  for (const section of ordered) {
    if (section.id === 'recents') continue;
    for (const item of section.items) {
      if (item.kind === 'leaf') {
        if (matchesMenuHref(currentPath, item.href, search)) return { sectionId: section.id, parentId: null };
      } else if (groupHoldsPage(item.children, currentPath, search)) {
        return { sectionId: section.id, parentId: item.id };
      }
    }
  }
  return null;
}

/** This browser's localStorage, or null where it cannot be reached. */
export function safeStorage(): Storage | null {
  try { return typeof window === 'undefined' ? null : window.localStorage; } catch { return null; }
}
